import React, { useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import OrderToast from '../demo/mockPos/OrderToast';
import StaffCallToast from '../demo/mockPos/StaffCallToast';
import ReviewCompleteCard from '../demo/mockPos/ReviewCompleteCard';

/**
 * 와우포스 관리자 앱 알림 미러 레이어.
 * 호출 + 주문 + 리뷰완료 를 화면 중앙 단일 스택에 표시.
 *   - 첫(가장 오래된) 카드의 중심을 화면 정중앙에 고정.
 *   - 새 알림은 그 위로만 쌓임 (column-reverse). 하단으로 내려오지 않음.
 * 리뷰 '작성중' UI는 앱에서 표시하지 않음(PC 전용).
 *
 * 관리자 주문내역 모달은 transform/zoom 컨테이너라 fixed 좌표가 왜곡됨 →
 * document.body 로 portal 하여 실제 viewport 기준으로 렌더.
 *
 * Props:
 *   orders:  [{ id, tableName, isNewOrder, menuItems, totalPrice, reviewBenefit }]
 *   reviews: [{ id, tableName, serviceMenu }]
 *   calls:   [{ id, tableName, callItems }]
 *   onDismissOrder(id) / onDismissReview(id) / onDismissCall(id)
 */
const WowposNotifyLayer = ({
  orders = [],
  reviews = [],
  calls = [],
  onDismissOrder,
  onDismissReview,
  onDismissCall,
}) => {
  const firstCardRef = useRef(null);
  const [firstCardHeight, setFirstCardHeight] = useState(0);

  // DOM 순서 = column-reverse 기준 아래→위. index 0(첫 자식)이 시각적 최하단.
  const items = [
    ...orders.map((o) => ({ key: `o-${o.id}`, type: 'order', data: o })),
    ...reviews.map((r) => ({ key: `r-${r.id}`, type: 'review', data: r })),
    ...calls.slice(-6).map((c) => ({ key: `c-${c.id}`, type: 'call', data: c })),
  ];

  // 첫 카드 높이 측정 → bottom: calc(50% - h/2) 로 첫 카드 중심을 화면 중앙에 고정.
  //
  // 재측정 조건에 첫 카드의 key 를 넣어야 한다. 개수만 보면, 개수가 그대로인 채
  // 첫 카드만 교체되는 경우(예: 테이블 초기화로 주문 팝업 1개가 빠지는 동시에
  // 새 주문 팝업 1개가 들어옴, 또는 첫 카드가 주문→호출 로 바뀜)에 재측정이 돌지
  // 않는다. 그러면 저장된 높이가 사라진 옛 카드 것이라 위치가 어긋나고,
  // ResizeObserver 도 이미 떨어져 나간 노드를 계속 관찰해 영영 갱신되지 않는다.
  // 측정값이 실제보다 작으면 그 차이의 절반만큼 카드가 위로 뜬다.
  const firstKey = items[0]?.key;
  useLayoutEffect(() => {
    const el = firstCardRef.current;
    if (!el) { setFirstCardHeight(0); return; }
    const measure = () => setFirstCardHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [firstKey, items.length]);

  const dismiss = (item) => {
    if (item.type === 'order') return onDismissOrder && onDismissOrder(item.data.id);
    if (item.type === 'review') return onDismissReview && onDismissReview(item.data.id);
    return onDismissCall && onDismissCall(item.data.id);
  };

  const renderCard = (item) => {
    const d = item.data;
    if (item.type === 'order') {
      return (
        <OrderToast
          tableName={d.tableName}
          isNewOrder={d.isNewOrder}
          menuItems={d.menuItems}
          totalPrice={d.totalPrice}
          reviewBenefit={d.reviewBenefit}
          reviewChannel={d.reviewChannel}
          onClose={() => dismiss(item)}
        />
      );
    }
    if (item.type === 'review') {
      return (
        <ReviewCompleteCard
          tableName={d.tableName}
          serviceMenu={d.serviceMenu}
          hasPhoto={d.hasPhoto}
          channel={d.channel}
          onClose={() => dismiss(item)}
        />
      );
    }
    return (
      <StaffCallToast
        tableName={d.tableName}
        callItems={d.callItems}
        onClose={() => dismiss(item)}
      />
    );
  };

  const layer = (
    <div className="wnl-root">
      {items.length > 0 && (
        <div
          className="wnl-center-stack"
          style={{ bottom: `calc(50% - ${firstCardHeight / 2}px)` }}
        >
          {items.map((item, i) => (
            <div
              key={item.key}
              ref={i === 0 ? firstCardRef : null}
              className="wnl-item"
            >
              {renderCard(item)}
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .wnl-root {
          position: fixed;
          inset: 0;
          z-index: 11000;
          pointer-events: none;
        }
        .wnl-center-stack {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column-reverse;
          align-items: center;
          gap: 12px;
          pointer-events: none;
        }
        .wnl-item {
          pointer-events: auto;
        }
      `}</style>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(layer, document.body);
};

export default WowposNotifyLayer;
